import React, { useEffect, useRef } from 'react';
import { ContainerDisposition } from './disposition';
import { getTheme, Icon, IStyle, ITheme, mergeStyleSets } from '@fluentui/react';
import { useReffedState } from '../../framework/hooks/reffed-state';
import { isTouchDevice } from '../../framework/utils/device';
import $ from 'jquery';

interface IDynamicDuoContainerProps {
  disposition: ContainerDisposition;
  topLeftContent?: JSX.Element;
  bottomRightContent?: JSX.Element;
  onContainerSizeChange?: (width: number, height: number) => void;
}

interface IResizeContext {
  rootDim: number;
  minContainerSize: number;
  maxContainerSize: number;
  primaryContainerDim: number;
  startingMousePos: number;
  horizontalDisposition: boolean;
  resizeWidth?: number;
  resizeHeight?: number;
}

interface ISizeReportContext {
  previousWidth: number;
  previousHeight: number;
  timeoutId: number | undefined;
}

const SIZE_LIMIT_COEFFICIENT = 0.25;
const SPLITTER_THICKNESS = isTouchDevice() ? 28 : 14;

const getClassNames = (theme: ITheme) => {

  const root: IStyle = {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'stretch',
    maxWidth: '100%',
    position: 'relative',
  };

  const rootVertical: IStyle = {
    ...root,
    flexDirection: 'row',
  };

  const rootHorizontal: IStyle = {
    ...root,
    flexDirection: 'column',
  };

  const containerVertical: IStyle = {
    width: `calc(50% - ${(SPLITTER_THICKNESS / 2)}px)`,
  };

  const containerHorizontal: IStyle = {
    height: `calc(50% - ${(SPLITTER_THICKNESS / 2)}px)`,
  };

  const splitter: IStyle = {
    backgroundColor: theme.palette.neutralQuaternaryAlt,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexBasis: 'auto',
  };

  const splitterVertical: IStyle = {
    ...splitter,
    width: SPLITTER_THICKNESS,
    cursor: 'col-resize',
  };

  const splitterHorizontal: IStyle = {
    ...splitter,
    height: SPLITTER_THICKNESS,
    cursor: 'row-resize',
  };

  const splitterIcon: IStyle = {
    fontSize: 24,
    color: theme.palette.neutralLighter,
    cursor: 'inherit',
    userSelect: 'none',
  };

  const touchGlass: IStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
  };

  return mergeStyleSets({
    rootVertical,
    rootHorizontal,
    containerVertical,
    containerHorizontal,
    splitterVertical,
    splitterHorizontal,
    splitterIcon,
    touchGlass,
  });
};

export const DynamicDuoContainer: React.FunctionComponent<IDynamicDuoContainerProps> = (props) => {
  const { disposition, onContainerSizeChange } = props;
  const theme = getTheme();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const primaryContainerRef = useRef<HTMLDivElement | null>(null);
  const touchGlassRef = useRef<HTMLDivElement | null>(null);
  const resizeContext = useRef<IResizeContext | undefined>(undefined);
  const sizeReportContext = useRef<ISizeReportContext>({
    previousHeight: -1,
    previousWidth: -1,
    timeoutId: undefined,
  })
  const [primaryContainerWidth, setPrimaryContainerWidth, primaryContainerWidthRef] = useReffedState<string | number | undefined>(undefined);
  const [primaryContainerHeight, setPrimaryContainerHeight, primaryContainerHeightRef] = useReffedState<string | number | undefined>(undefined);
  const [secondaryContainerWidth, setSecondaryContainerWidth, secondaryContainerWidthRef] = useReffedState<string | number | undefined>(undefined);
  const [secondaryContainerHeight, setSecondaryContainerHeight, secondaryContainerHeightRef] = useReffedState<string | number | undefined>(undefined);
  const previousDisposition = useRef<ContainerDisposition | undefined>(undefined);

  // disallow drag down screen refresh whenever container is in horizontal orientation
  useEffect(() => {
    if(disposition === ContainerDisposition.DualHorizontal) {
      const pageRoots = $('html, body');
      pageRoots.attr('style', 'overflow: hidden; overscroll-behavior: none;');
    }

    return () => {
      const pageRoots = $('html, body');
      pageRoots.removeAttr('style');
    };
  }, [disposition]);

  useEffect(() => {
    setPrimaryContainerWidth(undefined);
    setPrimaryContainerHeight(undefined);
    setSecondaryContainerWidth(undefined);
    setSecondaryContainerHeight(undefined);

    if(disposition === ContainerDisposition.Single) {
      setPrimaryContainerWidth('100%');
      setPrimaryContainerHeight('100%');
    } else if (disposition === ContainerDisposition.DualVertical && previousDisposition.current === ContainerDisposition.DualHorizontal) {
      setPrimaryContainerWidth(primaryContainerHeightRef.current);
      setSecondaryContainerWidth(secondaryContainerHeightRef.current);
    } else if (disposition === ContainerDisposition.DualHorizontal && previousDisposition.current === ContainerDisposition.DualVertical) {
      setPrimaryContainerHeight(primaryContainerWidthRef.current);
      setSecondaryContainerHeight(secondaryContainerWidthRef.current);
    }

    previousDisposition.current = disposition;
  }, [disposition,
    // the following will never change and are only here for compiler appeasement:
    setPrimaryContainerWidth,
    setPrimaryContainerHeight,
    setSecondaryContainerWidth,
    setSecondaryContainerHeight,
    primaryContainerWidthRef,
    primaryContainerHeightRef,
    secondaryContainerWidthRef,
    secondaryContainerHeightRef,
  ]);

  useEffect(() => {
    const cxt = sizeReportContext.current;
    const onTimeForSizeCheck = () => {
      const container = rootRef.current;
      if(container === null) { return; }

      const sizeChanged = (cxt.previousHeight !== container.clientHeight) || (cxt.previousWidth !== container.clientWidth);
      if(sizeChanged && !!onContainerSizeChange) {
        cxt.previousHeight = container.clientHeight;
        cxt.previousWidth = container.clientWidth;
        onContainerSizeChange(container.clientWidth, container.clientHeight);
      }

      cxt.timeoutId = window.setTimeout(onTimeForSizeCheck, 2000);
    };
    window.setTimeout(onTimeForSizeCheck, 100);

    return () => {
      if(cxt.timeoutId !== undefined) {
        window.clearTimeout(cxt.timeoutId);
      }
    };
  }, [onContainerSizeChange]);


  const classNames = getClassNames(theme);
  const rootClassName = disposition === ContainerDisposition.Single || disposition === ContainerDisposition.DualVertical ?
    classNames.rootVertical : classNames.rootHorizontal;
  const primaryContainerClassName = disposition === ContainerDisposition.Single || disposition === ContainerDisposition.DualVertical ?
    classNames.containerVertical : classNames.containerHorizontal;
  const splitterClassName = disposition === ContainerDisposition.DualVertical ?
    classNames.splitterVertical : classNames.splitterHorizontal;
  const secondaryContainerClassName = disposition === ContainerDisposition.DualVertical ?
    classNames.containerVertical : classNames.containerHorizontal;

  const onBeginResizing = (screenX: number, screenY: number) => {
    if(props.disposition === ContainerDisposition.DualHorizontal) {
      const rootDim = rootRef.current!.clientHeight;
      resizeContext.current = {
        rootDim,
        minContainerSize: rootDim * SIZE_LIMIT_COEFFICIENT,
        maxContainerSize: (rootDim * (1 - SIZE_LIMIT_COEFFICIENT)) - SPLITTER_THICKNESS,
        primaryContainerDim: primaryContainerRef.current!.clientHeight,
        startingMousePos: screenY,
        horizontalDisposition: true
      };
    } else {
      const rootDim = rootRef.current!.clientWidth;
      resizeContext.current = {
        rootDim,
        minContainerSize: rootDim * SIZE_LIMIT_COEFFICIENT,
        maxContainerSize: (rootDim * (1 - SIZE_LIMIT_COEFFICIENT)) - SPLITTER_THICKNESS,
        primaryContainerDim: primaryContainerRef.current!.clientWidth,
        startingMousePos: screenX,
        horizontalDisposition: false
      };
    }
  };

  const onSplitterMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    onBeginResizing(e.screenX, e.screenY);
  };

  const onSplitterTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    onBeginResizing(t.screenX, t.screenY);
  };

  const onEndResizing = () => {
    if(resizeContext.current === undefined) { return; }

    const cxt = resizeContext.current;
    if(cxt.horizontalDisposition && cxt.resizeHeight !== undefined) {
      const pctHeight = (cxt.resizeHeight * 100) / cxt.rootDim;
      const splitterPct = (SPLITTER_THICKNESS * 100) / cxt.rootDim;
      setPrimaryContainerHeight(`${pctHeight.toFixed(4)}%`);
      setSecondaryContainerHeight(`${(100 - (pctHeight + splitterPct)).toFixed(4)}%`);
    } else if(cxt.horizontalDisposition === false && cxt.resizeWidth !== undefined) {
      const pctWidth = (cxt.resizeWidth * 100) / cxt.rootDim;
      const splitterPct = (SPLITTER_THICKNESS * 100) / cxt.rootDim;
      setPrimaryContainerWidth(`${pctWidth.toFixed(4)}%`);
      setSecondaryContainerWidth(`${(100 - (pctWidth + splitterPct)).toFixed(4)}%`);
    }
    resizeContext.current = undefined;
  };

  const onResizeMove = (screenX: number, screenY: number) => {
    if(resizeContext.current === undefined) { return; }

    const cxt = resizeContext.current;
    const mousePos = cxt.horizontalDisposition ? screenY : screenX;
    const mouseDelta = mousePos - cxt.startingMousePos;
    let containerDim = cxt.primaryContainerDim + mouseDelta;
    containerDim = containerDim < cxt.minContainerSize ? cxt.minContainerSize : containerDim;
    containerDim = containerDim > cxt.maxContainerSize ? cxt.maxContainerSize : containerDim;

    if(cxt.horizontalDisposition) {
      cxt.resizeHeight = containerDim;
      setPrimaryContainerHeight(containerDim);
      setSecondaryContainerHeight(cxt.rootDim - (containerDim + SPLITTER_THICKNESS));
    } else {
      cxt.resizeWidth = containerDim;
      setPrimaryContainerWidth(containerDim);
      setSecondaryContainerWidth(cxt.rootDim - (containerDim + SPLITTER_THICKNESS));
    }
  }

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if(resizeContext.current === undefined) { return; }

    onResizeMove(e.screenX, e.screenY);
  }

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if(resizeContext.current === undefined) { return; }
    const touch = e.touches[0];

    onResizeMove(touch.screenX, touch.screenY);

    // mobile devices don't dispatch the mouse leave event, so we are going to end resizing on those
    // devices when it is discovered that we are not touching the 'glass' that appears on top of the main
    // editing container during a resize
    if(!!touchGlassRef.current &&
      (touchGlassRef.current !== window.document.elementFromPoint(touch.pageX, touch.pageY))) {
        onEndResizing();
    }

    // suppress screen 'pull down' effect on iOS devices
    e.stopPropagation();
    e.preventDefault();
  }

  return (
    <div
      className={rootClassName}
      ref={rootRef}
      onMouseUp={onEndResizing}
      onTouchEnd={onEndResizing}
      onMouseLeave={onEndResizing}
      onMouseMove={onMouseMove}
      onTouchMove={onTouchMove}
    >
      <div
        className={primaryContainerClassName}
        ref={primaryContainerRef}
        style={{ width: primaryContainerWidth, height: primaryContainerHeight }}
      >
        {props.topLeftContent}
      </div>
      {props.disposition !== ContainerDisposition.Single &&
      <div
        className={splitterClassName}
        onMouseDown={onSplitterMouseDown}
        onTouchStart={onSplitterTouchStart}
      >
        <Icon
          className={classNames.splitterIcon}
          iconName={props.disposition === ContainerDisposition.DualHorizontal ? 'GripperBarHorizontal' : 'GripperBarVertical'}
        />
      </div>}
      {props.disposition !== ContainerDisposition.Single &&
      <div
        className={secondaryContainerClassName}
        style={{ width: secondaryContainerWidth, height: secondaryContainerHeight }}
      >
        {props.bottomRightContent}
      </div>}
      {!!resizeContext.current &&
      <div
        ref={touchGlassRef}
        className={classNames.touchGlass}
        onTouchMove={onTouchMove}
        onTouchEnd={onEndResizing}
      />}
    </div>
  );
};
